import React, { Component } from 'react';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import PropTypes from 'prop-types';
import {
  isNil,
  get,
  findIndex,
  max,
  isEmpty,
  round,
  forEach,
  isEqual,
  filter,
  find,
  cloneDeep,
  last
} from 'lodash';
import turf from '@turf/turf';
import websocket from 'websocket';
import {
  adaptLineStringTimes,
  densifyLine,
  syncLineAtControlPoint
} from '../../../modules/sync-video-line';
import {
  deleteAutomaticAnnotations,
  saveVideoEntities
} from '../../../api/Annotations/annotation-methods-client';
import * as annotationOps from '../../../api/Annotations/annotation-operations';
import { ANNOTATION_EXPORT_FORMATS } from '../../../api/Annotations/Annotations';
import * as videoOps from '../../../api/Videos/video-operations';
import * as videoCalculate from '../../../api/Videos/videos-calculations';
import { VIDEO_TYPES, VIDEO_EXPORT_FORMATS } from '../../../api/Videos/Videos';

import { exportTextToLocalFile } from '../../../modules/export-import-files';
import {
  latlong2featurePoint,
  getClosestFeaturePointInCollection
} from '../../../modules/geojson-utilities';
import methodPromise from '../../../modules/method-promise-wrapper';

import Loading from '../../components/Loading';
import VideoVisualizer from '../../components/VideoVisualizer';

let exportTextToPDF;
let generateAnnotationsGeoJSON;
let generateAnnotationsKML;
let generateAnnotationsPDF;
let generateVideoKML;
let generateVideoPDF;
let getMapCoordinatesForPixelPoint;
let getVideoStyle;
let setVideoStyle;
let setYouTubeAPIVideoControls;
let showProjectedVideo;

const W3CWebSocket = websocket.w3cwebsocket;

const DISPLAY_BLOCK = 'block';
const DISPLAY_NONE = 'none';
const FREQUENCY = 250;
const INITIAL_VIDEO_POSITION_WIDTH = 800; // pixels
const INITIAL_VIDEO_POSITION_HEIGHT = 450; // pixels
const MARGIN = 38;
const MAX_NUMBER_OF_POINTS = 2400;
const PLAYBACK_QUALITY = 'hd1080';
const RESOLUTION = 16 / 9;
const TIME_TOLERANCE = 5;
const VIDEO_HEIGHT = `calc(${50 / RESOLUTION}vw - ${MARGIN / RESOLUTION}px)`;
const VIDEO_WIDTH = `calc(50vw - ${MARGIN}px)`;

class VideoVisualizerContainer extends Component {
  aiRequestclient = {};

  clickableDiv = {};

  mapDiv = {};

  nonClickableTxtDiv = {};

  player = {};

  selectedIndexes = new Set();

  speedSelect = {};

  timeFlow = {};

  txtPlayer = {};

  videoMap = {};

  videoYouTubeDiv = {};

  state = {
    aiRequestOngoing: false,
    aiRequestProgress: 0,
    annotationVisible: false,
    buttonStates: {
      addAnnotationButton: false,
      addLineButton: false,
      aiAnnotationsButton: false
    },
    clientModulesReady: false,
    currentFeature: turf.point(videoOps.getLatLng0(this.props.video)),
    domain: 120000000,
    featureArray: [turf.point(videoOps.getLatLng0(this.props.video))],
    isClickableDivHidden: false,
    isDataLoaded: false,
    isFirstLoad: true,
    isMapReady: false,
    isTxtVideoLoaded: false,
    isVideoLoaded: false,
    lastClickTime: new Date().getTime(),
    markerVisible: false,
    readOnly: false,
    refreshRate: 1000,
    selectedAnnotation: {},
    showAllAnnotations: true,
    showIAModal: false,
    speed: 1,
    speedArray: [1],
    templateForAI: {},
    updateContinuouslyMapLocation: true,
    updateMapLocation: false,
    updateVideo: false,
    videoDuration: videoOps.getDefaultDurationVideo(this.props.video),
    videoLines: [],
    videoTime: 0,
    yaw: 0
  };

  async componentDidMount() {
    const { clientModulesReady, isDataLoaded } = this.state;
    if (!clientModulesReady) await this.loadClientModules();
    if (!isDataLoaded) this.loadDataFile();

    window.addEventListener('resize', () => this.forceUpdate());
  }

  componentDidUpdate(prevProps, prevState) {
    const { video } = this.props;
    const {
      buttonStates,
      clientModulesReady,
      isTxtVideoLoaded,
      isVideoLoaded,
      selectedAnnotation,
      speed,
      updateVideo,
      videoTime
    } = this.state;
    const { video: prevVideo } = prevProps;
    const {
      buttonStates: prevButtonStates,
      selectedAnnotation: prevSelectedAnnotation,
      speed: prevSpeed,
      videoTime: prevVideoTime
    } = prevState;

    if (clientModulesReady) {
      if (isVideoLoaded) {
        if (updateVideo) {
          this.player.seekTo(videoTime * 1e-6);
        }
        if (speed === 0) {
          this.player.pauseVideo();
        } else {
          this.player.setPlaybackRate(speed);
          this.player.setPlaybackQuality(PLAYBACK_QUALITY);
          this.player.playVideo();
        }
      }

      if (isTxtVideoLoaded) {
        if (updateVideo) {
          this.txtPlayer.seekTo(videoTime * 1e-6);
        }
        if (speed === 0) {
          this.txtPlayer.pauseVideo();
        } else {
          this.txtPlayer.setPlaybackRate(speed);
          this.txtPlayer.playVideo();
        }
      }

      if (!isEqual(buttonStates, prevButtonStates)) {
        this.handleButtonStatesChange(buttonStates, selectedAnnotation);
      }

      if (!isEqual(selectedAnnotation, prevSelectedAnnotation)) {
        this.handleSelectedAnnotationChange(selectedAnnotation);
      }

      if (!isEqual(prevSpeed, speed) && speed !== 0 && !updateVideo) {
        if (!buttonStates.addLineButton) this.toggleButtonSwitch();
        this.changeVideoContainerStateProperty('selectedAnnotation', {});
        this.hideAnnotation();
      }

      if (Math.abs(prevVideoTime - videoTime) > TIME_TOLERANCE) {
        this.handleVideoTimeChange(videoTime);
        if (!buttonStates.addLineButton) this.toggleButtonSwitch();
        if (speed !== 0) this.hideAnnotation();
      }

      if (!isEqual(videoOps.getOption(video), videoOps.getOption(prevVideo))) {
        this.handleVideoTimeChange(videoTime);
      }

      this.updateVideoStyle();
    }
  }

  componentWillUnmount() {
    const { aiRequestOngoing } = this.state;

    clearInterval(this.timeFlow);
    if (aiRequestOngoing) this.aiRequestclient.close();
    this.aiRequestclient = null;
    this.clickableDiv = null;
    this.videoMap = null;
    this.mapDiv = null;
    this.nonClickableTxtDiv = null;
    this.player = null;
    this.selectedIndexes = null;
    this.speedSelect = null;
    this.timeFlow = null;
    this.txtPlayer = null;
    this.videoYouTubeDiv = null;
  }

  changeOption = event => {
    const { video } = this.props;
    try {
      methodPromise('videos.setOption', video._id, event.target.value);
    } catch (error) {
      Bert.alert(error.reason, 'danger');
    }
  };

  changeShowAllAnnotations = () =>
    this.setState(prevState => {
      return { showAllAnnotations: !prevState.showAllAnnotations };
    });

  changeVideoContainerStateProperty = (property, value) => this.setState({ [property]: value });

  downloadAnnotations = eventKey => {
    const { annotations } = this.props;
    const { videoLines } = this.state;

    let annotationsToExport = '';
    switch (eventKey) {
      case ANNOTATION_EXPORT_FORMATS.PDF:
        annotationsToExport = generateAnnotationsPDF(annotations);
        exportTextToPDF('annotations.pdf', annotationsToExport);
        break;
      case ANNOTATION_EXPORT_FORMATS.KML:
        annotationsToExport = generateAnnotationsKML({ annotations, lines: videoLines });
        exportTextToLocalFile('dataGeometry.kml', annotationsToExport);
        break;
      case ANNOTATION_EXPORT_FORMATS.GEOJSON:
        annotationsToExport = generateAnnotationsGeoJSON({ annotations });
        exportTextToLocalFile('annotations.geojson', annotationsToExport);
        break;

      default:
        break;
    }
  };

  downloadVideo = (element, eventKey) => {
    const { video } = this.props;
    let videoToExport = '';
    const nameFile =
      element === 'Video' ? `${videoOps.getName(video)}` : `${videoOps.getName(video)}_track`;
    switch (eventKey) {
      case VIDEO_EXPORT_FORMATS.PDF:
        videoToExport = generateVideoPDF(video, element);
        exportTextToPDF(`${videoOps.getName(video)}.pdf`, videoToExport);
        break;
      case VIDEO_EXPORT_FORMATS.KML:
        videoToExport = generateVideoKML({
          video,
          elementVideo: element
        });
        exportTextToLocalFile(`${nameFile}.kml`, videoToExport);
        break;
      case VIDEO_EXPORT_FORMATS.GEOJSON:
        if (element === 'Video') videoToExport = JSON.stringify(turf.feature(video));
        else videoToExport = JSON.stringify(turf.feature(videoOps.getTrack(video)));
        exportTextToLocalFile(`${nameFile}.geojson`, videoToExport);
        break;
      default:
        break;
    }
  };

  fetchTime = point => {
    const { featureArray } = this.state;

    const closestFeaturePoint = getClosestFeaturePointInCollection(
      latlong2featurePoint(point),
      turf.featureCollection(featureArray)
    );
    const newTime = get(closestFeaturePoint, 'properties.timeUS');

    this.setState({
      updateVideo: true,
      videoTime: newTime,
      currentFeature: closestFeaturePoint
    });

    setTimeout(() => this.setState({ updateVideo: false }), 200);
  };

  getVideoTime = () => {
    const { updateVideo, isVideoLoaded } = this.state;
    if (isVideoLoaded && !updateVideo && !isEmpty(this.player)) {
      this.setState({ videoTime: this.player.getCurrentTime() * 1e6 });
    }
  };

  handleButtonStatesChange = (buttonStates, selectedAnnotation) => {
    switch (true) {
      case buttonStates.addAnnotationButton:
        this.handlePointButtonClick();
        break;
      case buttonStates.addLineButton:
        this.hideAnnotation();
        this.handleLineButtonClick();
        break;
      case buttonStates.aiAnnotationsButton:
        this.hideAnnotation();
        this.setState({ showIAModal: true, speed: 0 });
        break;
      case isEmpty(selectedAnnotation):
        this.hideAnnotation();
        break;
      default:
        break;
    }
  };

  handleClickOnVideo = event => {
    const { buttonStates, currentFeature } = this.state;
    const videoCoordinates = [event.clientX, event.clientY];
    const boundingRect = this.clickableDiv.getBoundingClientRect();

    const newCoordinates = getMapCoordinatesForPixelPoint({
      videoCoordinates,
      feature: currentFeature,
      boundingRect
    });

    switch (true) {
      case buttonStates.addAnnotationButton:
        this.setNewPoint({ newCoordinates, videoCoordinates, boundingRect });
        break;

      case buttonStates.addLineButton:
        this.setNewLine({ newCoordinates });
        break;

      default:
        break;
    }
  };

  handleCloseAIModal = () => {
    const { aiRequestOngoing } = this.state;

    this.toggleButtonSwitch();
    if (aiRequestOngoing) {
      this.aiRequestclient.close();
    } else {
      this.setState({ showIAModal: false, templateForAI: {} });
    }
  };

  handleLineButtonClick = () => {
    this.setState(prevState => {
      const { videoLines: prevVideoLines } = prevState;

      const newVideoLines = cloneDeep(prevVideoLines);

      const newLine = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      };

      if (!isEqual(last(prevVideoLines), newLine)) newVideoLines.push(newLine);

      return { videoLines: newVideoLines };
    });
  };

  handlePointButtonClick = () => {
    const { annotations } = this.props;
    const { videoTime } = this.state;

    const isTimeOccupied = !isNil(
      find(
        annotations,
        annotation => Math.abs(annotationOps.getVideoTime(annotation) - videoTime) < TIME_TOLERANCE
      )
    );

    if (isTimeOccupied) {
      Bert.alert('Annotation already at this time', 'danger');
      this.toggleButtonSwitch();
    } else {
      Bert.alert('Click on the video to add an annotation', 'info');
    }
    this.setState({ speed: 0, updateMapLocation: true, updateVideo: true });
  };

  handleRequestAIAnnotations = () => {
    const { video, annotations } = this.props;
    const automaticAnnotations = filter(annotations, annotation =>
      annotationOps.isAutomatic(annotation)
    );
    if (
      isEmpty(automaticAnnotations) ||
      // eslint-disable-next-line no-alert
      window.confirm('This will delete the current detected entities.')
    ) {
      deleteAutomaticAnnotations(videoOps.getId(video));
      this.requestAIAnnotations();
    }
  };

  handleSelectAIIndex = event => {
    if (event.target.checked) {
      this.selectedIndexes.add(event.target.value);
    } else {
      this.selectedIndexes.delete(event.target.value);
    }
  };

  handleSelectedAnnotationChange = selectedAnnotation => {
    const { speed, annotationVisible, readOnly } = this.state;
    const unselectAnnotation = isEmpty(selectedAnnotation);

    this.setState({
      annotationVisible: unselectAnnotation ? false : annotationVisible,
      markerVisible: !unselectAnnotation,
      readOnly: unselectAnnotation || readOnly,
      speed: unselectAnnotation ? speed : 0,
      updateMapLocation: !unselectAnnotation,
      updateVideo: false
    });
  };

  handleVideoTimeChange = videoTime => {
    const { video } = this.props;
    const { featureArray, selectedAnnotation } = this.state;

    let currentFeatureIndex = findIndex(
      featureArray,
      feature => get(feature, 'properties.timeUS') >= videoTime
    );
    if (currentFeatureIndex < 0 || featureArray.length === 1 || videoOps.isStartPosition(video)) {
      currentFeatureIndex = 0;
    }
    let currentFeature = featureArray[currentFeatureIndex];
    if (
      !isEmpty(selectedAnnotation) &&
      !isEqual(currentFeature.properties.timeUS, selectedAnnotation.geoJSON.properties.videoTime)
    ) {
      const newDistance = videoCalculate.calculatePointDistanceInFeatures(
        featureArray,
        currentFeatureIndex,
        selectedAnnotation
      );
      const annotationGeoJson = annotationOps.getGeoJSON(selectedAnnotation);
      currentFeature = {
        geometry: annotationGeoJson.geometry,
        properties: {
          distanceTravelled: newDistance,
          isControlPoint: false,
          timeUS: annotationOps.getVideoTime(selectedAnnotation)
        },
        type: 'Feature'
      };
    }
    let yaw = 0;
    if (videoOps.isDevicePosition(video)) {
      let currentPoint = {};
      let nextPoint = {};
      if (currentFeatureIndex < featureArray.length - 2) {
        currentPoint = currentFeature;
        nextPoint = featureArray[currentFeatureIndex + 1];
      } else {
        currentPoint = featureArray[currentFeatureIndex - 1];
        nextPoint = currentFeature;
      }
      yaw = turf.bearing(currentPoint, nextPoint);
    }

    this.setState({ yaw, currentFeature });
  };

  hideAnnotation = () =>
    this.setState({
      annotationVisible: false,
      markerVisible: false,
      readOnly: true
    });

  hideShowClickableDiv = () => {
    const { isClickableDivHidden } = this.state;
    if (isClickableDivHidden) {
      this.clickableDiv.style.display = DISPLAY_BLOCK;
      this.nonClickableTxtDiv.style.display = DISPLAY_BLOCK;
    } else {
      this.clickableDiv.style.display = DISPLAY_NONE;
      this.nonClickableTxtDiv.style.display = DISPLAY_NONE;
    }
    this.setState({ isClickableDivHidden: this.clickableDiv.style.display === DISPLAY_NONE });
  };

  initializeLoop = () => {
    this.timeFlow = setInterval(this.getVideoTime, FREQUENCY);
  };

  onVideoReady = (event, playerName) => {
    const { featureArray, speed } = this.state;

    this[playerName] = event.getInternalPlayer();

    if (this[playerName].nodeType === 1) setYouTubeAPIVideoControls(this[playerName]);

    this[playerName].mute();
    this[playerName].playVideo();
    this[playerName].setPlaybackRate(speed);
    this[playerName].setPlaybackQuality(PLAYBACK_QUALITY);

    const newVideoDuration = this[playerName].getDuration() * 1e6; // Duration of video in microseconds
    this.setState({ videoDuration: newVideoDuration });
    const speedArray = this[playerName].getAvailablePlaybackRates();
    this.speedSelect.value = 1;

    const lastFeaturePointTime = get(last(featureArray), `properties.timeUS`);

    switch (playerName) {
      case 'txtPlayer':
        this.setState({ isTxtVideoLoaded: true });
        break;

      case 'player':
        this.setState({
          domain: max([newVideoDuration, lastFeaturePointTime]),
          isVideoLoaded: true,
          speedArray
        });
        break;

      default:
        break;
    }
  };

  playPause = () => {
    const { speed } = this.state;
    if (speed !== 0) {
      this.setState({ speed: 0 });
    } else {
      this.setState({ speed: Number(this.speedSelect.value) });
    }
  };

  sendToClosestPoint = event => {
    event.preventDefault();
    const point = {
      lat: event.target.form.latitude.value,
      lng: event.target.form.longitude.value
    };
    this.fetchTime(point);
  };

  setNewLine = ({ newCoordinates }) => {
    const { speed, lastClickTime, videoLines } = this.state;
    const currentClickTime = new Date().getTime();

    const newVideoLines = cloneDeep(videoLines);
    const currentLine = last(newVideoLines);

    if (currentClickTime - lastClickTime < 300) {
      if (speed === 0) {
        get(currentLine, `geometry.coordinates`).push(turf.getCoords(currentLine)[0]);
      }
      this.toggleButtonSwitch();
    } else {
      get(currentLine, `geometry.coordinates`).push(newCoordinates);
    }

    this.setState({ lastClickTime: currentClickTime, videoLines: newVideoLines });
  };

  setNewPoint = ({ newCoordinates, videoCoordinates, boundingRect }) => {
    const { videoTime, buttonStates } = this.state;
    const { top, left, width, height } = boundingRect;

    if (buttonStates.addAnnotationButton) {
      const selectedAnnotation = {
        geoJSON: turf.point(newCoordinates, {
          videoTime,
          videoPosition: {
            x: videoCoordinates[0] - left,
            y: videoCoordinates[1] - top,
            width,
            height
          }
        }),
        origin: 'manual'
      };

      this.setState({
        annotationVisible: true,
        markerVisible: true,
        readOnly: false,
        selectedAnnotation
      });
    }
  };

  setReferenceInVideoContainer = (variableKey, elementNode) => {
    this[variableKey] = elementNode;
  };

  showAnnotation = () =>
    this.setState({
      annotationVisible: true,
      markerVisible: true,
      readOnly: true
    });

  toggleButtonSwitch = (thisButton = '') => {
    this.setState(prevState => {
      const myButtons = Object.keys(prevState.buttonStates);
      const newButtonStates = cloneDeep(prevState.buttonStates);
      forEach(myButtons, button => {
        if (thisButton !== button) {
          if (prevState.buttonStates[button]) {
            newButtonStates[button] = false;
          }
        }
      });
      if (!isEmpty(thisButton)) {
        newButtonStates[thisButton] = !prevState.buttonStates[thisButton];
      }
      return { buttonStates: newButtonStates };
    });
  };

  updateVideoStyle = () => {
    const { video } = this.props;
    const { annotationVisible, currentFeature } = this.state;

    const showVideo = showProjectedVideo({
      currentFeature,
      video,
      isMapShown: !annotationVisible,
      leafletMap: this.videoMap
    });

    let videoStyle = {};
    if (videoOps.isProjectedVideo(video) && showVideo) {
      videoStyle = getVideoStyle({
        currentFeature,
        leafletMap: this.videoMap,
        videoHeight: isNil(this.videoYouTubeDiv)
          ? INITIAL_VIDEO_POSITION_HEIGHT
          : this.videoYouTubeDiv.offsetHeight,
        videoWidth: isNil(this.videoYouTubeDiv)
          ? INITIAL_VIDEO_POSITION_WIDTH
          : this.videoYouTubeDiv.offsetWidth
      });
    }
    setVideoStyle({ videoContainer: this.videoYouTubeDiv, videoStyle, showVideo });
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({
        generateAnnotationsGeoJSON,
        generateAnnotationsKML,
        generateAnnotationsPDF,
        generateVideoKML,
        generateVideoPDF,
        getMapCoordinatesForPixelPoint,
        getVideoStyle,
        setVideoStyle,
        setYouTubeAPIVideoControls,
        showProjectedVideo
      } = await import('../../../modules/geomotionvideo-operations'));
      ({ exportTextToPDF } = await import('../../../modules/client/export-import-files-client'));
      this.setState({ clientModulesReady: true });
    }
  }

  loadDataFile() {
    const { video } = this.props;
    const { refreshRate, videoDuration } = this.state;

    let newRefreshRate = refreshRate;
    const pointsInTrack = videoDuration / (newRefreshRate * 1000);
    if (pointsInTrack > MAX_NUMBER_OF_POINTS) {
      newRefreshRate *= pointsInTrack / MAX_NUMBER_OF_POINTS;
    }

    const dataToLoadFunction = () => {
      let newAllFeature;
      let featureArraySync;
      if (video.coordinatesDataType === 'point') {
        featureArraySync = [
          turf.point(videoOps.getLatLng0(video), {
            distanceTravelled: 0,
            isControlPoint: false,
            timeUS: 0
          })
        ];
        if (!isEmpty(video.controlPoints)) {
          featureArraySync = featureArraySync.concat(video.controlPoints.features);
        }
      } else {
        const controlPointsVideo = videoOps.getControlPoints(video);
        const newFeatureArray = adaptLineStringTimes({
          line: videoOps.getTrack(video),
          totalTime: videoDuration
        });

        newAllFeature = densifyLine({
          featureArray: newFeatureArray,
          refreshRate: newRefreshRate
        });

        if (controlPointsVideo.features.length > 2 && video.coordinatesDataType !== 'csvGoPro') {
          const filterControlPoints = filter(
            controlPointsVideo.features,
            (element, index, array) => index !== 0 && index !== array.length - 1
          );
          const lineSyncByControlPoint = filterControlPoints.map(element => {
            const syncLine = syncLineAtControlPoint({
              featureArray: newAllFeature,
              latLngPoint: {
                lng: element.geometry.coordinates[0],
                lat: element.geometry.coordinates[1]
              },
              videoTime: element.properties.timeUS
            });
            newAllFeature = syncLine.updatedFeatureArray;
            return newAllFeature;
          });

          featureArraySync = lineSyncByControlPoint[lineSyncByControlPoint.length - 1];
        } else {
          featureArraySync = newAllFeature;
        }
      }
      const videoData = {
        type: 'FeatureCollection',
        features: featureArraySync
      };
      return videoData.features;
    };

    const dataToLoad = dataToLoadFunction();
    this.setState({
      featureArray: dataToLoad,
      isDataLoaded: true,
      currentFeature: dataToLoad[0]
    });
  }

  // TODO: move this feature to server
  requestAIAnnotations() {
    const { video } = this.props;
    const { featureArray, templateForAI } = this.state;

    this.aiRequestclient = new W3CWebSocket(Meteor.settings.public.requestDetectionURL);

    this.aiRequestclient.onerror = () => {
      this.setState({ aiRequestOngoing: false });
      Bert.alert('Connection error', 'danger');
      throw new Error('Connection error');
    };

    this.aiRequestclient.onopen = () => {
      const indexesToDetect = [];
      this.selectedIndexes.forEach(index => indexesToDetect.push(index));

      const dataToSend = { url: videoOps.getURL(video), indexes: indexesToDetect };
      this.aiRequestclient.send(JSON.stringify(dataToSend));

      this.setState({ aiRequestOngoing: true });
      Bert.alert('This may take a few minutes. Please wait', 'info');
    };

    this.aiRequestclient.onclose = () => {
      this.setState({ aiRequestOngoing: false, aiRequestProgress: 0 });
    };

    this.aiRequestclient.onmessage = async event => {
      const { status, element: videoEntities, progress } = JSON.parse(event.data);
      this.setState({ aiRequestProgress: round(progress, 1) });

      switch (status) {
        case 'Finished':
          await saveVideoEntities(
            videoOps.getId(video),
            videoEntities,
            templateForAI,
            featureArray
          );
          setTimeout(() => {
            this.setState({ showIAModal: false, templateForAI: {} });
          }, 1000);
          Bert.alert(`Process finished. ${videoEntities.length} entities detected.`, 'success');
          break;

        case 'Server Error':
          Bert.alert('Server error', 'danger');
          break;

        case 'Server is Busy':
          Bert.alert('Server is busy. Please try again later.', 'danger');
          break;

        default:
          break;
      }
    };
  }

  render() {
    const { video } = this.props;
    const { annotationVisible, clientModulesReady, currentFeature, isDataLoaded } = this.state;

    const clickadivWidth = isNil(this.clickableDiv)
      ? INITIAL_VIDEO_POSITION_WIDTH
      : this.clickableDiv.offsetWidth;
    const clickadivHeight = isNil(this.clickableDiv)
      ? INITIAL_VIDEO_POSITION_HEIGHT
      : this.clickableDiv.offsetHeight;

    let showVideo = false;
    if (clientModulesReady && !isEmpty(this.videoMap)) {
      showVideo = showProjectedVideo({
        currentFeature,
        video,
        isMapShown: !annotationVisible,
        leafletMap: this.videoMap
      });
    }
    if (isDataLoaded) {
      return (
        <VideoVisualizer
          clickadivHeight={clickadivHeight}
          clickadivWidth={clickadivWidth}
          selectedIndexes={this.selectedIndexes}
          showVideo={showVideo}
          videoHeight={VIDEO_HEIGHT}
          videoWidth={VIDEO_WIDTH}
          changeOption={this.changeOption}
          changeShowAllAnnotations={this.changeShowAllAnnotations}
          changeVideoContainerStateProperty={this.changeVideoContainerStateProperty}
          downloadAnnotations={this.downloadAnnotations}
          downloadVideo={this.downloadVideo}
          fetchTime={this.fetchTime}
          handleClickOnVideo={this.handleClickOnVideo}
          handleCloseAIModal={this.handleCloseAIModal}
          handleRequestAIAnnotations={this.handleRequestAIAnnotations}
          handleSelectAIIndex={this.handleSelectAIIndex}
          hideShowClickableDiv={this.hideShowClickableDiv}
          initializeLoop={this.initializeLoop}
          onVideoReady={this.onVideoReady}
          playPause={this.playPause}
          sendToClosestPoint={this.sendToClosestPoint}
          showAnnotation={this.showAnnotation}
          setReferenceInVideoContainer={this.setReferenceInVideoContainer}
          toggleButtonSwitch={this.toggleButtonSwitch}
          updateVideoStyle={this.updateVideoStyle}
          {...this.props}
          {...this.state}
        />
      );
    }
    return <Loading />;
  }
}

VideoVisualizerContainer.defaultProps = {
  video: {
    name: '',
    description: '',
    project: '',
    url: '',
    type: VIDEO_TYPES.VIDEOWITHINFO
  }
};

VideoVisualizerContainer.propTypes = {
  video: PropTypes.object,
  annotations: PropTypes.array.isRequired,
  isPublic: PropTypes.bool.isRequired
};

export default VideoVisualizerContainer;
